import { Swiper, SwiperSlide } from "swiper/react/swiper-react.js";
import { Autoplay, Pagination } from "swiper";
import TouchableOpacity from "../../components/TouchableOpacity/TouchableOpacity";
import Image from "../../components/Image/Image";
import { calculateImageWidth, convertARGBtoRGBA, getInitialScale } from "../../utils/Utils";
import { Props } from "./model";
import { FeatureLineOne, FeatureLineTwo, FeatureText, StyledImageBanner, TextContainer } from "./styled";
import useGenericContentStore from "../../store/genericContent.store";
import useScreenDimensions from "../../hooks/useScreenDimensions";
import { dark_gray, styleHelper } from "../../StyleHelpers";

const imageStyleNarrow = {
	width: "31.0625rem",
	height: "5.0625rem",
	flexShrink: 0,
};

const imageStyle = {
	width: "36.5625rem",
	height: "5.9325rem",
	flexShrink: 0,
};

const Banner = (props: Props) => {
	const { isNarrow } = useGenericContentStore((state: any) => ({ isNarrow: state.isNarrow }));
	const { width } = useScreenDimensions();

	const bannerStyles = {
		marginLeft: props.isPreview ? 97 : 152,
		marginRight: 20,
		borderRadius: 16,
		aspectRatio: 4 / 1,
		outlineStyle: "solid",
		outlineColor: "rgba(255, 255, 255, 0.30)",
		outlineWidth: 2,
		outlineOffset: -2,
		backgroundColor: dark_gray,
	};

	/**
	 * Calculates the number of items to display based on screen width.
	 * - Returns a fixed value for wide screens.
	 * - Scales proportionally for narrow screens.
	 *
	 * @returns number - The calculated number of items to display.
	 */
	const calculateItemsByWidth = () => {
		if (!isNarrow) return 1.83;
		return width / (864 * getInitialScale());
	};

	/**
	 * Returns the height for a banner based on screen width.
	 * - Uses a fixed height for narrow screens.
	 * - Returns "auto" for wider screens.
	 *
	 * @returns string | number - The calculated banner height.
	 */
	const getHeight = () => (isNarrow ? styleHelper.wide.banner.height : "auto");

	/**
	 * Styles for a banner image, adjusting for screen width.
	 * - Chooses width based on whether the screen is narrow or wide.
	 * - Uses `getHeight()` to determine height.
	 * - Sets a fixed aspect ratio of 4:1.
	 */
	const styleImageNarrow = {
		width: isNarrow ? styleHelper.narrow.banner.width : styleHelper.wide.banner.width,
		height: getHeight(),
		aspectRatio: "4/1",
	};

	/**
	 * Renders feature text or STT (speech-to-text) overlay for a banner.
	 * - Filters out any empty text fields from `featureText`.
	 * - If no text or STT exists, returns null.
	 * - Displays Line1 and Line2 text with styles adjusted for narrow or wide screens.
	 * - If STT URL exists, renders it as an image instead of Line1 text.
	 *
	 * @param obj - The banner object containing `featureText` array and optional `sttUrl`.
	 * @returns JSX.Element | null - The rendered feature text component or null if nothing to display.
	 */
	const renderFeatureText = (obj: any) => {
		const featureText = obj.featureText?.filter((f: any) => f.text) ?? [];
		const stt = obj?.sttUrl;

		if (!featureText.length && !stt) return null;

		const lineOne = featureText.find((f: any) => f.name === "Line1" && f.text.trim());
		const lineTwo = featureText.find((f: any) => f.name === "Line2" && f.text.trim());

		const stylesLayout = isNarrow ? styleHelper.narrow.banner : styleHelper.wide.banner;

		return (
			<FeatureText height={stylesLayout.height} isNarrow={isNarrow}>
				<TextContainer>
					{stt ? (
						<Image
							source={{
								uri: `${stt}?width=${calculateImageWidth(false, true)}&disablewebp=false`,
							}}
							resizeMode="contain"
							style={isNarrow ? imageStyleNarrow : imageStyle}
						/>
					) : (
						lineOne && (
							<FeatureLineOne
								fontSize={stylesLayout.lineOne.fontSize}
								lineHeight={stylesLayout.lineOne.lineHeight}
								hexCode={convertARGBtoRGBA(lineOne.hexCode)}
							>
								{lineOne.text}
							</FeatureLineOne>
						)
					)}

					{lineTwo && (
						<FeatureLineTwo
							fontSize={stylesLayout.lineTwo.fontSize}
							lineHeight={stylesLayout.lineTwo.lineHeight}
							hexCode={convertARGBtoRGBA(lineTwo.hexCode)}
						>
							{lineTwo.text}
						</FeatureLineTwo>
					)}
				</TextContainer>
			</FeatureText>
		);
	};

	/**
	 * Renders a single banner item inside a Swiper slide.
	 * - Handles click via `props.onPress` if `parentProductId` exists.
	 * - Updates hover state to apply border styles.
	 * - Displays feature text and the banner image, adjusting width and styling for narrow screens.
	 *
	 * @param product - The banner product object containing `parentProductId`, `packshots`, and `imageUrl`.
	 * @param index - The index of the banner in the Swiper for tabIndex and key.
	 * @returns JSX.Element - The rendered SwiperSlide with the banner content.
	 */
	const renderBanner = (product: any, index: number) => (
		<SwiperSlide key={`hero-banner${product.parentProductId}`} tabIndex={index}>
			<TouchableOpacity
				activeOpacity={1}
				onPress={() => (product.parentProductId ? props.onPress(product.parentProductId) : {})}
				style={{ cursor: product.parentProductId ? "pointer" : "arrow" }}
			>
				{renderFeatureText(product)}
				<StyledImageBanner
					imageBanner={`${product?.packshots || product?.imageUrl}?width=${calculateImageWidth(false, true)}&disablewebp=false`}
					style={[isNarrow && styleImageNarrow, bannerStyles, isNarrow && { marginLeft: 1 }]}
				/>
			</TouchableOpacity>
		</SwiperSlide>
	);

	return (
		<Swiper
			style={{ width: "100%" }}
			slidesPerView={isNarrow ? calculateItemsByWidth() : 1}
			centeredSlides={true}
			spaceBetween={isNarrow ? 16 : 1}
			pagination={{
				clickable: true,
			}}
			modules={[Autoplay, Pagination]}
		>
			{props.item.isBanner &&
				props.item?.products.length !== 0 &&
				props.item?.products?.slice(0, 1).map((product: any, index: number) => renderBanner(product, index))}
		</Swiper>
	);
};

export default Banner;
